import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

// all tripway partners
export const fetchTripwayPartners = createAsyncThunk(
  "tripway_partners/fetchAll",
  async (page = 1, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/partners?page=${page}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data; // Return the whole response including pagination
    } catch (error) {
      const message = error.response?.data?.message || "Failed load data";
      return rejectWithValue(message);
    }
  }
);


// add tripway partner
export const addTripwayPartner = createAsyncThunk(
  "tripway_partners/add",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/partners`, userData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue(message);
    }
  }
);
// get tripway partner record
export const tripwayPartnerRecord = createAsyncThunk(
  "tripway_partners/tripwayPartnerRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/partners/${id}`);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue({ message });
    }
  }
);
// update tripway partner 
export const updateTripwayPartner = createAsyncThunk(
  "tripway_partners/updateTripwayPartner",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/partners/update/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue(message);
    }
  }
);
// delete tripway partner
export const deleteTripwayPartner = createAsyncThunk(
  "tripway_partners/deleteTripwayPartner",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${BASE_URL}/partners/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete tripway partner";
      return rejectWithValue({ message });
    }
  }
);
const TripwayPartnersSlice = createSlice({
  name: "tripway_partners",
  initialState: {
    tripway_partners: [],
    record: null,
    isLoading: false,
    errorDelete: null,
    successDelete: null,
    error: null,
    success: null,
    pagination: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTripwayPartners.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTripwayPartners.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tripway_partners = action.payload.data.data;
        state.pagination = action.payload.data.pagination;
      })
      .addCase(fetchTripwayPartners.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      .addCase(addTripwayPartner.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addTripwayPartner.fulfilled, (state, action) => {
        state.isLoading = false;
        const p = action.payload;
        state.success =
          (typeof p === "string" ? p : null) ||
          p?.message ||
          p?.data?.message ||
          "";
      })
      .addCase(addTripwayPartner.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add data";
      })
      .addCase(tripwayPartnerRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(tripwayPartnerRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.record = action.payload.data;
      })
      .addCase(tripwayPartnerRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to add data";
      })
      .addCase(updateTripwayPartner.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateTripwayPartner.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(updateTripwayPartner.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          action.payload ||
          "Failed to update record";
      })
      .addCase(deleteTripwayPartner.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteTripwayPartner.fulfilled, (state, action) => {
        state.isLoading = false;

        // Remove from data array inside tripway partners
        if (state.tripway_partners?.data) {
          state.tripway_partners.data = state.tripway_partners.data.filter(
            (record) => record.id !== action.meta.arg
          );
        }

        state.successDelete = action.payload?.message || "Deleted successfully";
      })
      .addCase(deleteTripwayPartner.rejected, (state, action) => {
        state.isLoading = false;
        state.errorDelete =
          action.payload?.message ||
          action.payload ||
          action.error?.message ||
          "Failed to delete tripway partner";
      });
  },
});

export const { clearState } = TripwayPartnersSlice.actions;
export default TripwayPartnersSlice.reducer;
